import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { adminApi, errorMessage } from "../../lib/api";
import { useMatches } from "../matching/useMatches";
import { MatchAlert, ScoreBadge } from "../matching/MatchAlert";
import { WorkerCard } from "./WorkerSearch";

const MatchGroup = ({ title, items, testId, fee, act }) => items.length > 0 && (
  <div data-testid={testId} className="space-y-3">
    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">{title} ({items.length})</p>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((m) => (
        <div key={m.id} data-testid={`match-${m.id}`} className={m.tier === "top" ? "ring-1 ring-orange-500/70" : ""}>
          <div className="flex items-center justify-between bg-[#1E293B] px-5 py-2">
            <ScoreBadge score={m.score} testId={`match-score-${m.id}`} />
            {m.new && <span className="font-mono text-[10px] uppercase text-emerald-300">New</span>}
          </div>
          <WorkerCard w={m.worker} fee={fee} scope={`match-${m.id}-`} onUnlock={(x) => act(`/employer/workers/${x.id}/unlock`, "Contact unlocked")}
            onHire={(x) => act(`/employer/workers/${x.id}/hire`, "Joining OTP generated. Ask the employee for the 4-digit code at site.")} />
        </div>
      ))}
    </div>
  </div>
);

export const EmployerMatches = ({ refresh, fee, onChange }) => {
  const { data, reload, alertOpen, markSeen } = useMatches("/employer/matches", refresh);
  const [listing, setListing] = useState("");
  if (!data) return null;
  const items = data.items.filter((m) => !listing || m.listing_id === listing);
  const roleOf = (id) => data.listings.find((l) => l.id === id)?.job_role;
  const act = (path, msg) => adminApi.post(path).then(() => { toast.success(msg); reload(); onChange(); }).catch((e) => toast.error(errorMessage(e)));
  return (
    <section data-testid="employer-matches" className="space-y-4">
      <MatchAlert open={alertOpen} count={data.new_count} onView={markSeen}
        lines={data.items.filter((m) => m.new).map((m) => ({ id: m.id, score: m.score, text: `${m.worker.name} · ${roleOf(m.listing_id)}` }))} />
      <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-white"><Sparkles className="h-5 w-5 text-orange-400" />Instant Matches</h2>
      <div className="flex flex-wrap gap-2">
        {[{ id: "", label: "All requirements" }, ...data.listings.map((l) => ({ id: l.id, label: `${l.job_role} · ${l.district} (${l.reference})` }))].map((l) => (
          <button key={l.id || "all"} onClick={() => setListing(l.id)} data-testid={`match-listing-${l.id || "all"}`}
            className={`border px-3 py-1.5 text-sm ${listing === l.id ? "border-orange-500 text-orange-200" : "border-white/10 text-slate-400 hover:border-orange-500/60"}`}>{l.label}</button>
        ))}
      </div>
      {items.length === 0 && <p data-testid="employer-matches-empty" className="py-6 text-slate-500">No matches yet. We match KYC-approved employees automatically as they register.</p>}
      <MatchGroup title="Top Match · 80%+" testId="matches-top" items={items.filter((m) => m.tier === "top")} fee={fee} act={act} />
      <MatchGroup title="Shortlisted · 60-79%" testId="matches-shortlist" items={items.filter((m) => m.tier !== "top")} fee={fee} act={act} />
    </section>
  );
};
